// src/pages/document/DocumentRequestAcknowledgmentPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@react/components';
import { UserPicker } from '@/components/UserPicker';

type Mode = 'simple' | 'pipeline';

interface PipelineStageForm {
  id: string;
  name: string;
  description: string;
  action: string;
  completion_rule: string;
  threshold_count: number;
  actors: { id: string; name: string }[];
  require_comment: boolean;
  allow_delegation: boolean;
  timeout_hours: number;
}

const ACTIONS = [
  { value: 'DOCUMENT_PIPELINE_ACTION_ACKNOWLEDGE', labelKey: 'document.pipeline.action.acknowledge' },
  { value: 'DOCUMENT_PIPELINE_ACTION_APPROVE', labelKey: 'document.pipeline.action.approve' },
  { value: 'DOCUMENT_PIPELINE_ACTION_REVIEW', labelKey: 'document.pipeline.action.review' },
  { value: 'DOCUMENT_PIPELINE_ACTION_SIGN', labelKey: 'document.pipeline.action.sign' },
];

const COMPLETION_RULES = [
  { value: 'PIPELINE_COMPLETION_RULE_ALL', labelKey: 'document.pipeline.rule.all' },
  { value: 'PIPELINE_COMPLETION_RULE_ANY', labelKey: 'document.pipeline.rule.any' },
  { value: 'PIPELINE_COMPLETION_RULE_THRESHOLD', labelKey: 'document.pipeline.rule.threshold' },
];

function createEmptyStage(): PipelineStageForm {
  return {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    action: 'DOCUMENT_PIPELINE_ACTION_ACKNOWLEDGE',
    completion_rule: 'PIPELINE_COMPLETION_RULE_ALL',
    threshold_count: 1,
    actors: [],
    require_comment: false,
    allow_delegation: false,
    timeout_hours: 0,
  };
}

/**
 * Document request acknowledgment page.
 * Supports two modes:
 * 1. Simple — send acknowledgment request to actors directly
 * 2. Pipeline — create a multi-stage pipeline with sequential stages
 */
export const DocumentRequestAcknowledgmentPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useDocument();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<Mode>('simple');

  // Simple mode state
  const [formData, setFormData] = useState({
    actorId: '',
    actorName: '',
    due_date: '',
    message: '',
  });

  // Pipeline mode state
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineMessage, setPipelineMessage] = useState('');
  const [pipelineDueDate, setPipelineDueDate] = useState('');
  const [stages, setStages] = useState<PipelineStageForm[]>([createEmptyStage()]);
  const [existingPipelines, setExistingPipelines] = useState<{ id: string; name: string; stages: number }[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [pipelineSource, setPipelineSource] = useState<'new' | 'existing'>('new');

  useEffect(() => {
    if (id) {
      store.ListPipelines().then((resp) => {
        const pipelines = resp.pipelines?.pipelines;
        if (pipelines) {
          setExistingPipelines(pipelines.map((p) => ({
            id: p.entity?.id ?? '',
            name: p.name ?? '',
            stages: p.stages?.length || 0,
          })));
        }
      }).catch(() => {});
    }
  }, [id, store]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addStage = () => {
    setStages((prev) => [...prev, createEmptyStage()]);
  };

  const removeStage = (index: number) => {
    if (stages.length <= 1) return;
    setStages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateStage = (index: number, field: keyof PipelineStageForm, value: PipelineStageForm[keyof PipelineStageForm]) => {
    setStages((prev) => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const addActorToStage = (index: number, actorId: string, actorName: string) => {
    setStages((prev) => prev.map((s, i) => {
      if (i !== index) return s;
      if (s.actors.some((a) => a.id === actorId)) return s;
      return { ...s, actors: [...s.actors, { id: actorId, name: actorName }] };
    }));
  };

  const removeActorFromStage = (stageIndex: number, actorId: string) => {
    setStages((prev) => prev.map((s, i) => {
      if (i !== stageIndex) return s;
      return { ...s, actors: s.actors.filter((a) => a.id !== actorId) };
    }));
  };

  const handleSubmitSimple = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !formData.actorId) return;
    setIsSubmitting(true);
    try {
      await store.RequestAcknowledgment('document', id, {
        actors: {
          actors: [{ entity: { id: formData.actorId, type_id: '', type_name: 'profile', platform_id: '', tenant_id: '', product_id: '' }, display_name: formData.actorName, avatar: '' }],
        },
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
        message: formData.message || undefined,
      });
      navigate(`/documents/${id}/acknowledgments`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitPipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    try {
      let pipelineId = selectedPipelineId;

      if (pipelineSource === 'new') {
        const pipeline = await store.CreatePipeline({
          name: pipelineName,
          stages: stages.map((s, i) => ({
            id: s.id,
            name: s.name,
            description: s.description,
            order: i + 1,
            action: s.action,
            completion_rule: s.completion_rule,
            threshold_count: s.completion_rule === 'PIPELINE_COMPLETION_RULE_THRESHOLD' ? s.threshold_count : undefined,
            actors: { actors: s.actors.map((a) => ({ entity: { id: a.id }, display_name: a.name, avatar: '' })) },
            require_comment: s.require_comment,
            allow_delegation: s.allow_delegation,
            timeout: s.timeout_hours > 0 ? `${s.timeout_hours * 3600}s` : undefined,
            notify_on_entry: true,
          })),
        } as never);
        pipelineId = pipeline.pipeline?.entity?.id ?? '';
      }
      if (!pipelineId) throw new Error('Pipeline id is missing');

      await store.StartPipeline('document', id, pipelineId, {
        message: pipelineMessage || undefined,
        due_date: pipelineDueDate ? new Date(pipelineDueDate).toISOString() : undefined,
      });
      navigate(`/documents/${id}/acknowledgments`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">
            {t('document.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}/acknowledgments`} className="text-muted-foreground hover:text-foreground">
            {t('document.acknowledgments')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.requestAcknowledgment')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.requestAcknowledgment')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Mode selector */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'simple' ? 'primary' : 'secondary'}
          onClick={() => setMode('simple')}
        >
          {t('document.pipeline.modeSimple')}
        </Button>
        <Button
          type="button"
          variant={mode === 'pipeline' ? 'primary' : 'secondary'}
          onClick={() => setMode('pipeline')}
        >
          {t('document.pipeline.modePipeline')}
        </Button>
      </div>

      {mode === 'simple' ? (
        <form onSubmit={handleSubmitSimple}>
          <Card>
            <CardHeader>
              <CardTitle>{t('document.requestAcknowledgment')}</CardTitle>
              <CardDescription>{t('document.requestAcknowledgmentDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <UserPicker
                label={t('document.ackActor')}
                value={formData.actorId}
                onChange={(userId, displayName) => setFormData((prev) => ({ ...prev, actorId: userId, actorName: displayName }))}
                placeholder={t('document.ackActorPlaceholder')}
              />
              <Input
                label={t('document.ackDueDate')}
                type="date"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
                placeholder={t('document.ackDueDatePlaceholder')}
              />
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">{t('document.ackComment')}</label>
                <textarea
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[100px]"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder={t('document.ackCommentPlaceholder')}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Link to={`/documents/${id}/acknowledgments`}>
                <Button type="button" variant="secondary">{t('common.cancel')}</Button>
              </Link>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('common.submitting') : t('common.submit')}
              </Button>
            </CardFooter>
          </Card>
        </form>
      ) : (
        <form onSubmit={handleSubmitPipeline}>
          {/* Pipeline source selector */}
          <Card className="mb-4">
            <CardContent className="pt-6">
              <div className="flex gap-4 items-center">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pipelineSource"
                    checked={pipelineSource === 'new'}
                    onChange={() => setPipelineSource('new')}
                    className="accent-primary"
                  />
                  <span className="text-sm font-medium">{t('document.pipeline.createNew')}</span>
                </label>
                {existingPipelines.length > 0 && (
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="pipelineSource"
                      checked={pipelineSource === 'existing'}
                      onChange={() => setPipelineSource('existing')}
                      className="accent-primary"
                    />
                    <span className="text-sm font-medium">{t('document.pipeline.useExisting')}</span>
                  </label>
                )}
              </div>
            </CardContent>
          </Card>

          {pipelineSource === 'existing' ? (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>{t('document.pipeline.selectPipeline')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={selectedPipelineId} onValueChange={setSelectedPipelineId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('document.pipeline.selectPipelinePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {existingPipelines.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.stages} {t('document.pipeline.stages')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Pipeline name */}
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle>{t('document.pipeline.title')}</CardTitle>
                  <CardDescription>{t('document.pipeline.description')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Input
                    label={t('document.pipeline.name')}
                    value={pipelineName}
                    onChange={(e) => setPipelineName(e.target.value)}
                    placeholder={t('document.pipeline.namePlaceholder')}
                    required
                  />
                </CardContent>
              </Card>

              {/* Stages */}
              {stages.map((stage, index) => (
                <Card key={stage.id} className="mb-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{index + 1}</Badge>
                        <CardTitle className="text-base">
                          {stage.name || t('document.pipeline.stageUntitled')}
                        </CardTitle>
                      </div>
                      {stages.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeStage(index)}>
                          {t('common.remove')}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Input
                      label={t('document.pipeline.stageName')}
                      value={stage.name}
                      onChange={(e) => updateStage(index, 'name', e.target.value)}
                      placeholder={t('document.pipeline.stageNamePlaceholder')}
                      required
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-foreground">
                          {t('document.pipeline.stageAction')}
                        </label>
                        <Select value={stage.action} onValueChange={(v) => updateStage(index, 'action', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACTIONS.map((a) => (
                              <SelectItem key={a.value} value={a.value}>
                                {t(a.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-foreground">
                          {t('document.pipeline.completionRule')}
                        </label>
                        <Select
                          value={stage.completion_rule}
                          onValueChange={(v) => updateStage(index, 'completion_rule', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPLETION_RULES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                {t(r.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {stage.completion_rule === 'PIPELINE_COMPLETION_RULE_THRESHOLD' && (
                      <Input
                        label={t('document.pipeline.thresholdCount')}
                        type="number"
                        min={1}
                        value={String(stage.threshold_count)}
                        onChange={(e) => updateStage(index, 'threshold_count', parseInt(e.target.value) || 1)}
                      />
                    )}

                    {/* Actors */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        {t('document.pipeline.stageActors')}
                      </label>
                      {stage.actors.map((actor) => (
                        <div key={actor.id} className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
                          <span className="text-sm flex-1">{actor.name}</span>
                          <button
                            type="button"
                            onClick={() => removeActorFromStage(index, actor.id)}
                            className="text-muted-foreground hover:text-destructive text-xs"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                      <UserPicker
                        label=""
                        value=""
                        onChange={(userId, displayName) => addActorToStage(index, userId, displayName)}
                        placeholder={t('document.pipeline.addActorPlaceholder')}
                      />
                    </div>

                    {/* Options row */}
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={stage.require_comment}
                          onChange={(e) => updateStage(index, 'require_comment', e.target.checked)}
                          className="accent-primary"
                        />
                        <span className="text-sm">{t('document.pipeline.requireComment')}</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={stage.allow_delegation}
                          onChange={(e) => updateStage(index, 'allow_delegation', e.target.checked)}
                          className="accent-primary"
                        />
                        <span className="text-sm">{t('document.pipeline.allowDelegation')}</span>
                      </label>
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-foreground">
                        {t('document.pipeline.stageDescription')}
                      </label>
                      <textarea
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px]"
                        value={stage.description}
                        onChange={(e) => updateStage(index, 'description', e.target.value)}
                        placeholder={t('document.pipeline.stageDescriptionPlaceholder')}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button type="button" variant="secondary" onClick={addStage} className="w-full mb-4">
                + {t('document.pipeline.addStage')}
              </Button>
            </>
          )}

          {/* Pipeline execution settings */}
          <Card>
            <CardHeader>
              <CardTitle>{t('document.pipeline.executionSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label={t('document.pipeline.dueDate')}
                type="date"
                value={pipelineDueDate}
                onChange={(e) => setPipelineDueDate(e.target.value)}
              />
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-foreground">
                  {t('document.pipeline.messageToActors')}
                </label>
                <textarea
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]"
                  value={pipelineMessage}
                  onChange={(e) => setPipelineMessage(e.target.value)}
                  placeholder={t('document.pipeline.messagePlaceholder')}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Link to={`/documents/${id}/acknowledgments`}>
                <Button type="button" variant="secondary">{t('common.cancel')}</Button>
              </Link>
              <Button
                type="submit"
                disabled={isSubmitting || (pipelineSource === 'existing' && !selectedPipelineId)}
              >
                {isSubmitting ? t('common.submitting') : t('document.pipeline.startPipeline')}
              </Button>
            </CardFooter>
          </Card>
        </form>
      )}
    </div>
  );
});

export default DocumentRequestAcknowledgmentPage;
